import {Injectable} from '@angular/core';
import _ from 'lodash';
import {ClientService} from './client.service';
import {
  IndividualNonApplicantModel,
  IndividualPersonModel,
  IndividualPhoneModel, IndividualRelationshipModel,
  PersonalDetailsModel
} from '../models/widgets/personal-details-model';
import {FormBuilderService} from './form-builder.service';
import {
  ApplicationTypeModel, BusinessIncomeModel,
  CompanyDetailsModel,
  HomeLoanDetailsModel,
  IncomeDetailsModel, IndividualCompanyModel,
  IndividualSecurityModel, IndividualSplitModel,
  OriginatorBrokerModel,
  ProductModel,
  SecurityDetailsModel,
  SolicitorBorrowerDetailsModel,
  SolicitorPanelDetailsModel
} from '../models/widgets';
import {BorrowerTypeEnhancedModel} from '../models/widgets/borrower-type-enhanced-model';
import {IndividualTrustModel, TrustDetailsModel} from '../models/widgets/trust-details-model';
import {DocumentDetailsModel} from '../models/widgets/documents-upload-model';
import {
  BasicIncomeModel,
  EmploymentDetailsModel,
  EmploymentGroupsModel, SelfEmployedAddbackModel,
  SingleEmploymentModel, SinglePreviousEmploymentModel
} from '../models/widgets/employment-details-model';
import {IndividualInvestmentIncomeModel, InvestmentIncomeDetailsModel} from '../models/widgets/investment-income-model';
import {AssetsDetailsModel, IndividualAssetModel} from '../models/widgets/assets-details-model';
import {IndividualLiabilityModel, LiabilitiesDetailsModel} from '../models/widgets/liabilities-details-model';
import {ExpensesDetailsModel, IndividualExpenseModel} from '../models/widgets/expenses-details-model';
import {SubmitModel} from '../models/widgets/submit-model';
import {BrokerNotesModel} from '../models/widgets/broker-notes-model';
import {TypesService} from './types.service';
import {firstValueFrom, Observable} from 'rxjs';
import {UntypedFormArray, UntypedFormControl, Validators} from '@angular/forms';

const FMC_ORIGINATOR = 176946;
const LCA_ORIGINATOR = 134;
const ORIGINATOR_PARTY_ROLE_TYPE_IDS: any = [72, 74, 76];
const BROKER_PARTY_ROLE_TYPE_IDS: any = [73, 75, 77, 87, 88, 146];
const BROKER_CONTACT_PARTY_ROLE_TYPE_IDS: any = [91];
const SALES_PERSON_PARTY_ROLE_TYPE_IDS: any = [136];
let OTHER_INCOME_SOURCE_TYPES: any[] = [];
let NON_TAXABLE_INCOME_SOURCE_TYPES: any[] = [];
const MOBILE = 3;
const BORROWER_TYPES = {
  INDIVIDUAL: 'individual',
  JOINT_INDIVIDUAL: 'joint-individual',
  NAS: 'individual-with-non-applicant'
};


@Injectable()
export class ApplicationMappingService {
  public typesList: any = ['income-source-types' , 'countries', 'australian-state-types', 'credit-providers', 'anzsic-categories'];
  public addressRecords: any = [];
  public incomeSourceTypes: any = [];
  public australianStateTypes: any = [];
  public countries: any = [];
  public addbackIncomeTypes: any = [];
  public creditProviders: any = [];
  public anzsicCategories: any = [];
  public justiceApplication = false;

  constructor(public clientService: ClientService,
              public formBuilderService: FormBuilderService,
              public typesService: TypesService) {
  }

  protected getWidgetMapper() {
    return {
      'app-application-type': new ApplicationTypeModel(),
      'app-assets-details': new AssetsDetailsModel(),
      'app-borrower-type-enhanced': new BorrowerTypeEnhancedModel(),
      'app-broker-notes': new BrokerNotesModel(),
      'app-company-details': new CompanyDetailsModel(),
      'app-docs-upload': new DocumentDetailsModel(),
      'app-employment-details': new EmploymentDetailsModel(),
      'app-expenses-details': new ExpensesDetailsModel(),
      'app-home-loan-details': new HomeLoanDetailsModel(),
      'app-income-details': new IncomeDetailsModel(),
      'app-investment-income': new InvestmentIncomeDetailsModel(),
      'app-liabilities-details': new LiabilitiesDetailsModel(),
      'app-originator-broker': new OriginatorBrokerModel(),
      'app-personal-details': new PersonalDetailsModel(),
      'app-product': new ProductModel(),
      'app-security-details': new SecurityDetailsModel(),
      'app-solicitor-borrower-details': new SolicitorBorrowerDetailsModel(),
      'app-solicitor-panel-details': new SolicitorPanelDetailsModel(),
      'app-submit': new SubmitModel(),
      'app-trust-details': new TrustDetailsModel()
    };
  }

  /**
   * Takes a Shape A packet and maps to an Application Wizard formGroup model
   * @param submissionModel
   * @param baseModel
   * @param appId
   * @param faApplicationType
   * @param justiceApplication
   */
  generateFormGroupModel(submissionModel: any, baseModel: any, appId: any, faApplicationType: any, justiceApplication: boolean): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.justiceApplication = justiceApplication;
      // fetch any required types data before processing the submission packet
      this.getTypesData().subscribe((result: any) => {
        this.addbackIncomeTypes = this.incomeSourceTypes.filter(x => x.ext.incomeCategory === 'addback');

        const promises: Promise<any>[] = [];
        promises.push(this.setupBorrowerAndApplicationTypes(submissionModel, appId, faApplicationType));
        promises.push(this.mapPartyObject(submissionModel));

        if (faApplicationType !== 'new-security') {
          promises.push(this.mapCollateralPropertyObject(submissionModel));
          promises.push(this.mapProductObject(submissionModel, justiceApplication));
        }

        Promise.all(promises).then((formGroupArray: any[]) => {
          console.log('formGroupArray', formGroupArray);

          const formGroup = formGroupArray.reduce((prev, curr) => {
            return {...prev, ...curr};
          }, {});

          this.convertToBaseModel(baseModel, formGroup)
            .then((faBaseModel: any) => {
              resolve(faBaseModel);
            });
        }, (reason: any) => {
          console.log('formGroup generation failed - ', reason);
          reject(reason);
        });
      });
    });
  }

  /**
   * Converts the mapped data to a new baseModel
   * @param baseModel
   * @param newModel
   */
  convertToBaseModel(baseModel, newModel): Promise<any> {
    console.log('convertToBaseModel - convertToBaseModel', baseModel);
    console.log('convertToBaseModel - newModel', newModel);

    return new Promise<any>((resolve, reject) => {
      try {
        const totalSteps = Object.keys(baseModel).length;
        let completedSteps = 0;

        Object.keys(baseModel).forEach((step: any) => {
          Object.keys(baseModel[step]).forEach((formModel: any) => {
            const camelCasedKey = _.camelCase(formModel);
            if (newModel[camelCasedKey]) {
              baseModel[step][formModel] = newModel[camelCasedKey];
              console.log('matched model', baseModel[step][formModel]);
            }
          });
          completedSteps++;
        });

        if (completedSteps === totalSteps) {
          resolve(baseModel);
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * This sets up the application type and borrower type data for the form
   * @param submissionModel
   * @param appId
   * @param faApplicationType
   */
  setupBorrowerAndApplicationTypes(submissionModel, appId, faApplicationType): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const appApplicationType = this.getWidgetMapper()['app-application-type'];
      const appBorrowerTypeEnhanced = this.getWidgetMapper()['app-borrower-type-enhanced'];
      const appBorrowerTypeEnhancedSmsf = this.getWidgetMapper()['app-borrower-type-enhanced'];
      const {parties, purposes} = submissionModel;

      // map applicationType
      appApplicationType.applicationType.value = 'old-personal';
      appApplicationType.furtherAdvanceApplicationType.value = faApplicationType;
      appApplicationType.appId.value = appId;
      appApplicationType.isStaffApplication.value = !!(purposes && purposes[0] && purposes[0].isStaffApplication);

      // clear borrowerTypeEnhanceSmsf validation
      Object.keys(appBorrowerTypeEnhancedSmsf).forEach((key) => {
        appBorrowerTypeEnhancedSmsf[key].validation = Validators.compose([]);
      });

      let existingIndividuals: any[] = _.filter(parties, (x: any) => x.type === 'individual' && !x.position);
      const existingTrust: any[] = _.filter(parties, (x: any) => x.type === 'company' && x.trust);
      let existingCompany: any[] = _.filter(parties, (x: any) => x.type === 'company' && !x.trust);

      // Array of roles that we are interested in
      const rolesToCheck = ['applicant', 'nonApplicant'];

      const correspondingMembers = _.filter(purposes[0].members, (member) => {
        return rolesToCheck.includes(member.role) &&
          _.some(existingIndividuals, (individual) => individual.partyRoleId === member.id);
      });

      // updated the existing individuals array where correspondingMembers don't match
      if (existingIndividuals.length !== correspondingMembers.length) {
        existingIndividuals = _.filter(existingIndividuals, (individual: any) =>
          _.some(correspondingMembers, { id: individual.partyRoleId })
        );
      }

      // ensure the company record is under purposes.members
      if (existingCompany.length > 0) {
        const correspondingCompanyMembers = _.filter(purposes[0].members, (member) => {
          return _.some(existingCompany, { partyRoleId: member.id });
        });

        existingCompany = _.filter(existingCompany, (company) =>
          _.some(correspondingCompanyMembers, { id: company.partyRoleId })
        );
      }

      // borrowerType ia for an individual
      if (existingIndividuals.length && !existingTrust.length && !existingCompany.length) {
        if (existingIndividuals.length === 1) {
          appBorrowerTypeEnhanced.borrowerType.value = BORROWER_TYPES.INDIVIDUAL;
        } else {
          if (correspondingMembers.find((x: any) => x.role === 'nonApplicant')) {
            appBorrowerTypeEnhanced.borrowerType.value = BORROWER_TYPES.NAS;
          } else {
            appBorrowerTypeEnhanced.borrowerType.value = BORROWER_TYPES.JOINT_INDIVIDUAL;
          }
        }

        // appBorrowerTypeEnhanced.borrowerType.value = (existingIndividuals.length === 1) ? 'individual' : 'joint-individual';
      } else if (existingTrust.length && existingCompany.length) {
        // check there are no trusts as smsf
        if (_.find(existingTrust, (x: any) => x.trustPurposeType === 'smsf-trust')) {
          reject('has-smsf');
        }

        appBorrowerTypeEnhanced.borrowerType.value = 'trust-with-company';
        const trustPurposeResult = _.find(existingTrust, (x: any) => x.trustPurposeType);
        appBorrowerTypeEnhanced.trustType.value = trustPurposeResult.trustPurposeType;
        appBorrowerTypeEnhanced.trustABN.value = (trustPurposeResult.acn && this.validateAbnAcn(trustPurposeResult.acn, 'acn')) ? trustPurposeResult.acn : null;
        appBorrowerTypeEnhanced.trustNoABN.value = !trustPurposeResult.acn;
      } else if (!existingTrust.length && existingCompany.length) {
        const companyResult = _.find(existingCompany, (x: any) => x.trustPurposeType);

        console.log('companyResult', JSON.stringify(companyResult, null, 2));

        appBorrowerTypeEnhanced.borrowerType.value = 'company';
        appBorrowerTypeEnhanced.companyACN.value = (existingCompany[0] && existingCompany[0].acn) ? _.trim(existingCompany[0].acn) : null;
        appBorrowerTypeEnhanced.acnRegistered.value = (existingCompany[0] && existingCompany[0].acnRegistered) ? existingCompany[0].acnRegistered : null;
      } else if (existingTrust.length && !existingCompany.length) {
        appBorrowerTypeEnhanced.borrowerType.value = 'trust-with-individual';

        const trustPurposeResult = _.find(existingTrust, (x: any) => x.trustPurposeType);
        appBorrowerTypeEnhanced.trustType.value = trustPurposeResult.trustPurposeType;
        appBorrowerTypeEnhanced.trustABN.value = (trustPurposeResult.abn && this.validateAbnAcn(trustPurposeResult.abn)) ? _.trim(trustPurposeResult.abn) : null;
        appBorrowerTypeEnhanced.trustNoABN.value = !trustPurposeResult.abn;
      }

      appBorrowerTypeEnhanced.borrowerCount.value = existingIndividuals.length;

      resolve({appApplicationType, appBorrowerTypeEnhanced, appBorrowerTypeEnhancedSmsf});
    });
  }

  /**
   * Map submission model parties object to their formGroups
   * @param submissionModel
   */
  mapPartyObject(submissionModel: any): Promise<any> {
    return new Promise<any>(async(resolve, reject) => {
      const appOriginatorBroker = this.getWidgetMapper()['app-originator-broker'];
      const appPersonalDetails = this.getWidgetMapper()['app-personal-details'];
      const appTrustDetails = this.getWidgetMapper()['app-trust-details'];
      const appCompanyDetails = this.getWidgetMapper()['app-company-details'];
      const appEmploymentDetails = this.getWidgetMapper()['app-employment-details'];
      const appAssetsDetails = this.getWidgetMapper()['app-assets-details'];
      const appLiabilitiesDetails = this.getWidgetMapper()['app-liabilities-details'];
      const appExpensesDetails = this.getWidgetMapper()['app-expenses-details'];
      const appInvestmentIncome = this.getWidgetMapper()['app-investment-income'];
      const appIncomeDetails = this.getWidgetMapper()['app-income-details'];
      const servicingModel =  {appAssetsDetails, appLiabilitiesDetails, appExpensesDetails, appInvestmentIncome};

      const {parties, servicing, purposes} = submissionModel;

      if (!this.justiceApplication) {
        this.addServicing(servicingModel, servicing[0], submissionModel, parties);
      }

      // update trusteeOf before processing party array
      const partyWithTrustees: any = await parties.filter((x: any) => x.trustees && x.trustees.length > 0);
      partyWithTrustees.forEach((party: any) => {
        party.trustees.forEach((trustee: any) => {
          const foundParty: any = parties.find((x: any) => +(x.id) === +(trustee));
          if (foundParty) {
            foundParty.trusteeOf = party.id;
          }
        });
      });

      // iterate over the party object
      for (const party of parties) {
        const index: number = parties.indexOf(party);
        // build the app-originator-broker formGroup model
        if (appOriginatorBroker && party.type === 'reference' && party.referenceType === 'partyRoleId') {
          const partyRoleData: any = await this.validatePartyRole(party.value);

          if (partyRoleData) {
            // patch originator data
            if (partyRoleData.partyRole && partyRoleData.partyRole.PartyRoleType && ORIGINATOR_PARTY_ROLE_TYPE_IDS.includes(partyRoleData.partyRole.PartyRoleType.iD)) {
              appOriginatorBroker.originator.value = {id: partyRoleData.partyRole.iD, name: _.trim(partyRoleData.party.company.companyName)};
              this.addCollateralLiability(servicingModel, submissionModel, appOriginatorBroker.originator.value);
            }

            // patch broker data
            if (partyRoleData.partyRole && partyRoleData.partyRole.PartyRoleType && BROKER_PARTY_ROLE_TYPE_IDS.includes(partyRoleData.partyRole.PartyRoleType.iD)) {
              appOriginatorBroker.broker.value = {id: partyRoleData.partyRole.iD, name: _.trim(partyRoleData.party.party.name)};
            }

            // patch broker contact data
            if (partyRoleData.partyRole && partyRoleData.partyRole.PartyRoleType && BROKER_CONTACT_PARTY_ROLE_TYPE_IDS.includes(partyRoleData.partyRole.PartyRoleType.iD)) {
              appOriginatorBroker.contact.value = {id: partyRoleData.partyRole.iD, name: _.trim(partyRoleData.party.party.name)};
            }

            // patch sales person data
            if (partyRoleData.partyRole && partyRoleData.partyRole.PartyRoleType && SALES_PERSON_PARTY_ROLE_TYPE_IDS.includes(partyRoleData.partyRole.PartyRoleType.iD)) {
              appOriginatorBroker.salesPerson.value = {id: partyRoleData.partyRole.iD, name: _.trim(partyRoleData.party.party.name)};
            }
          }
        }

        // ensure party exists as applicant or nonApplicant on the purpose.members object
        const rolesToCheck = ['applicant', 'nonApplicant'];
        const foundPurposeMember = _.find(purposes[0].members, (member: any) => {
          return member.id === party.partyRoleId && rolesToCheck.includes(member.role);
        });

        if (foundPurposeMember) {
          // add to individual
          if (appPersonalDetails && party.type === 'individual' && !party.position) {
            console.log('party - individual', party);

            if (party.memberType && party.memberType === 'non-applicant') {
              await this.addNewNAS(party, appPersonalDetails.personList, appEmploymentDetails, servicingModel, servicing, parties);
            } else {
              await this.addNewIndividual(party, appPersonalDetails.personList, appEmploymentDetails, servicingModel, servicing, parties);
            }

            console.log('appPersonalDetails', appPersonalDetails);
          }

          // add to company
          if (appCompanyDetails && party.type === 'company' && !party.trust) {
            await this.addNewCompany(party, appCompanyDetails.companyList, parties);
          }

          // add to trust
          if (appTrustDetails && ((party.type === 'trust') || (party.type === 'company' && party.trust))) {
            await this.addNewTrust(party, appTrustDetails.trustList, servicingModel, servicing, appIncomeDetails, parties);
          }

          if (parties.length === (index + 1)) {
            if (servicing[0].relationships.length) {
              await this.mapRelationshipObject(appPersonalDetails.personList.value, appCompanyDetails.companyList.value, appTrustDetails.trustList.value, servicing[0].relationships);
            }

            resolve({
              appOriginatorBroker,
              appPersonalDetails,
              appTrustDetails,
              appCompanyDetails,
              appEmploymentDetails,
              appAssetsDetails,
              appLiabilitiesDetails,
              appExpensesDetails,
              appInvestmentIncome
            });
          }
        }
      }
    });
  }

  /**
   * Map relationship object after parties have been mapped
   * @param personList
   * @param companyList
   * @param trustList
   * @param relationships
   */
  mapRelationshipObject(personList: any[], companyList: any[], trustList: any[], relationships: any[]): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      _.each(personList, (person: any, index: number) => {
        const fromRelationships = _.filter(relationships, (relationship: any) => relationship.from === person.partyRoleId.value);
        const toRelationships = _.filter(relationships, (relationship: any) => relationship.to === person.partyRoleId.value);

        if (fromRelationships.length) {
          console.log('fromRelationships', fromRelationships);
          _.each(fromRelationships, (relationship: any) => {
            const relationshipModel: any = new IndividualRelationshipModel();
            const toPersonIndex = _.findIndex(personList, (x: any) => x.partyRoleId.value === relationship.to);

            relationshipModel.from.value = (index + 1); // values are temporarily mapped against the index
            relationshipModel.to.value = (toPersonIndex >= 0) ? (toPersonIndex + 1) : null; // this is for the scenario where the relationship is either single or NAS
            relationshipModel.type.value = relationship.type;
            person.relationships.value.push(relationshipModel);
          });
        }

        if (toRelationships.length) {
          console.log('toRelationships', toRelationships);
          _.each(toRelationships, (relationship: any) => {
            const relationshipModel: any = new IndividualRelationshipModel();
            const fromPersonIndex = _.findIndex(personList, (x: any) => x.partyRoleId.value === relationship.from);

            if (fromPersonIndex >= 0) {
              relationshipModel.from.value = (index + 1); // values are temporarily mapped against the index
              relationshipModel.to.value = (fromPersonIndex + 1);
              relationshipModel.type.value = relationship.type;
              person.relationships.value.push(relationshipModel);
            }
          });
        }

        if (personList.length === (index + 1)) {
          resolve(true);
        }
      });
    });
  }

  /**
   * Maps the parent product
   * @param submissionModel
   * @param justiceApplication
   */
  mapProductObject(submissionModel: any, justiceApplication: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const {purposes, insurance, mortgageInsurance} = submissionModel;
      const appHomeLoanDetails = this.getWidgetMapper()['app-home-loan-details'];
      const appProduct = this.getWidgetMapper()['app-product'];

      if (justiceApplication) {
        appProduct.imported.value = true;
      }

      console.log('mortgageInsurance', JSON.stringify(mortgageInsurance, null, 2));
      console.log('insurance', JSON.stringify(insurance, null, 2));

      if (purposes.length) {
        if (mortgageInsurance || (insurance && insurance.mortgageInsurance)) {
          const lmi: any = (mortgageInsurance) ? mortgageInsurance : insurance.mortgageInsurance;
          delete lmi.premium;
          delete lmi.gst;
          delete lmi.stampDuty;

          appHomeLoanDetails.mortgageInsurance.value = lmi;
        }

        for (const purpose of purposes) {
          const index: number = purposes.indexOf(purpose);
          const splitModel = new IndividualSplitModel();

          appHomeLoanDetails.splits.value.push(splitModel);

          if (purposes.length === (index + 1)) {
            resolve({appHomeLoanDetails, appProduct});
          }
        }
      }
    });
  }

  /**
   * Map the collateral object
   * @param submissionModel
   */
  mapCollateralPropertyObject(submissionModel): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {
      const {collaterals} = submissionModel;
      const appSecurityDetails = this.getWidgetMapper()['app-security-details'];

      for (const collateral of collaterals) {
        const index: number = collaterals.indexOf(collateral);
        const securityModel = new IndividualSecurityModel();
        securityModel.link.value = collateral.id;
        securityModel.collateralId.value = collateral.id;
        securityModel.securityId.value = (collateral.externalRef) ? collateral.externalRef : null;
        securityModel.propertyNotFound.value = false;
        securityModel.mortgageType.value = collateral.mortgageType;
        securityModel.occupancy.value = collateral.occupancyType;
        securityModel.securityType.value = collateral.propertyType;
        securityModel.securityStatus.value = collateral.propertyStatusType;
        securityModel.propertyBeingPurchased.value = collateral.isBeingPurchased;

        if (collateral.address) {
          const crumbsAddress: any = (collateral.address.id) ? await this.getCrumbsAddressRecord(collateral.address.id) : null;
          securityModel.securityAddress.value = (crumbsAddress && crumbsAddress.addressText) ? this.mapAddressModel(crumbsAddress) : this.mapAddressModel(collateral.address);
        }

        console.log('securityModel', JSON.stringify(securityModel, null, 2));

        appSecurityDetails.securityList.value.push(securityModel);

        if (collaterals.length === (index + 1)) {
          resolve({appSecurityDetails});
        }
      }
    });
  }

  /**
   * Validates the partyRoleId is still active and returns the party and partyRole records
   * @param partyRoleId
   */
  validatePartyRole(partyRoleId): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.clientService.retrievePartyInfoFromPartyRole(partyRoleId)
        .then((partyRole: any) => {
          resolve(partyRole);
        }, (error: any) => {
          console.error('error retrieving partyRole data', error);
          reject();
        });
    });
  }

  async addNewNAS(data: any, personList: any, employmentList: any, servicingModel: any, servicingData: any, parties: any) {
    const personModel = new IndividualNonApplicantModel();
    personModel.firstName.value = (data.firstName) ? data.firstName : null;
    personModel.middleName.value = (data.otherNames) ? data.otherNames : null;
    personModel.lastName.value = (data.lastName) ? data.lastName : null;
    personModel.partyRoleId.value = (data.partyRoleId) ? data.partyRoleId : null;

    // contact details
    if (data.contact) {
      personModel.email.value = (data.contact.email) ? data.contact.email : null;

      const addPhoneContact = function(phoneNumber: any, phoneType: string) {
        const phoneModel = new IndividualPhoneModel();
        phoneModel.phoneNumber.value = phoneNumber;
        phoneModel.phoneType.value = phoneType;
        personModel.phones.value.push(phoneModel);
      };

      if (data.contact.mobile && data.contact.mobile.length > 0) {
        _.forEach(data.contact.mobile, function (value: any) {
          addPhoneContact(value, 'mobile');
        });
      }

      if (data.contact.fixed && data.contact.fixed.length > 0) {
        _.forEach(data.contact.fixed, function (value: any) {
          addPhoneContact(value, 'fixed');
        });
      }
    }

    personList.value.push(personModel);

    if (!this.justiceApplication) {
      this.addEmploymentIncome(data, employmentList, servicingModel, servicingData[0], parties);
    }
  }

  /**
   * Add new individual and add to the personList
   * @param data
   * @param personList
   * @param employmentList
   * @param servicingModel - {appAssetsDetails, appLiabilitiesDetails, appExpensesDetails, addInvestmentIncomeModel}
   * @param servicingData - servicing data from the submission model
   * @param parties
   */
  async addNewIndividual(data: any, personList: any, employmentList: any, servicingModel: any, servicingData: any, parties: any) {
    const personModel = new IndividualPersonModel();
    personModel.firstName.value = (data.firstName) ? data.firstName : null;
    personModel.middleName.value = (data.otherNames) ? data.otherNames : null;
    personModel.lastName.value = (data.lastName) ? data.lastName : null;
    personModel.dependentsUnder11.value = (data.dependentsYoung) ? data.dependentsYoung : 0;
    personModel.dependentsOver11.value = (data.dependents) ? data.dependents : 0;
    personModel.maritalStatus.value = (data.maritalStatus) ? data.maritalStatus : null;
    personModel.auCitizen.value = (data.auCitizen) ? data.auCitizen : false;
    personModel.partyRoleId.value = (data.partyRoleId) ? data.partyRoleId : null;
    personModel.partyId.value = (data.partyId) ? data.partyId : null;
    personModel.borrowerGuarantor.value = (data.memberType) ? data.memberType : null;
    personModel.auCitizen.value = !!(data.nonResident);
    personModel.dateOfBirth.validationAsync = null;

    if (data?.address?.residential) {
      const crumbsAddress: any = (data.address.residential.id) ? await this.getCrumbsAddressRecord(data.address.residential.id) : null;
      personModel.homeAddress.value = (crumbsAddress && crumbsAddress.addressText) ? this.mapAddressModel(crumbsAddress) : this.mapAddressModel(data.address.residential);
      personModel.residencyType.value = (data?.address?.residential) ? data.address.residential.residencyType : null;
    }

    if (data?.address?.postal) {
      const crumbsAddress: any = (data.address.postal.id) ? await this.getCrumbsAddressRecord(data.address.postal.id) : null;
      personModel.postalAddress.value = (crumbsAddress && crumbsAddress.addressText) ? this.mapAddressModel(crumbsAddress) : this.mapAddressModel(data.address.postalAddress);
    }

    if (data?.address?.postSettlement) {
      const crumbsAddress: any = (data.address.postSettlement.id) ? await this.getCrumbsAddressRecord(data.address.postSettlement.id) : null;
      personModel.postSettlementAddress.value = (crumbsAddress && crumbsAddress.addressText) ? this.mapAddressModel(crumbsAddress) : this.mapAddressModel(data.address.postSettlement);
    }

    if (data?.address?.postal && data?.address?.residential && data?.address?.postal.id !== data?.address?.residential.id) {
      personModel.postalAddressAsHome.value = false;
    }

    if (data?.address?.residential && data?.address?.postSettlement && data?.address?.residential.id !== data?.address?.postSettlement.id) {
      personModel.postSettlementAddressAsHome.value = false;
    }

    if (data?.address?.previous && data?.address?.previous.length > 0) {
      const addressPromises = data.address.previous.map(async (previous: any) => {
        const crumbsAddress: any = previous.id ? await this.getCrumbsAddressRecord(previous.id) : null;
        const addressModel: any = crumbsAddress && crumbsAddress.addressText ? this.mapAddressModel(crumbsAddress) : this.mapAddressModel(previous);
        personModel.previousAddressList.value.push(addressModel);
      });

      await Promise.all(addressPromises);
    }

    // contact details
    if (data.contact) {
      personModel.email.value = (data.contact.email) ? data.contact.email : null;

      const addPhoneContact = function(phoneNumber: any, phoneType: string) {
        const phoneModel = new IndividualPhoneModel();
        phoneModel.phoneNumber.value = phoneNumber;
        phoneModel.phoneType.value = phoneType;
        personModel.phones.value.push(phoneModel);
      };

      if (data.contact.mobile && data.contact.mobile.length > 0) {
        _.forEach(data.contact.mobile, function (value: any) {
          addPhoneContact(value, 'mobile');
        });
      }

      if (data.contact.fixed && data.contact.fixed.length > 0) {
        _.forEach(data.contact.fixed, function (value: any) {
          addPhoneContact(value, 'fixed');
        });
      }
    }

    personList.value.push(personModel);

    if (!this.justiceApplication) {
      this.addEmploymentIncome(data, employmentList, servicingModel, servicingData[0], parties);
    }
  }

  /**
   * Add new trust to the trustList
   * @param data
   * @param trustList
   * @param servicingModel
   * @param servicingData
   * @param appIncomeDetails
   * @param parties
   */
  async addNewTrust(data: any, trustList: any, servicingModel: any, servicingData: any, appIncomeDetails: any, parties: any) {
    const trustModel = new IndividualTrustModel();
    trustModel.trustName.value = (data.name) ? data.name : null;
    trustModel.trustABN.value = (data.abn && this.validateAbnAcn(data.abn)) ? _.trim(data.abn) : null;
    trustModel.trustType.value = (data.trustPurposeType) ? data.trustPurposeType : null;
    trustModel.partyRoleId.value = (data.partyRoleId) ? data.partyRoleId : null;
    trustModel.partyId.value = (data.partyId) ? data.partyId : null;

    // get contact and contact's details
    if (data.contact) {
      const contactPartyDetails: any = await this.validatePartyRole(data.contact);
      trustModel.trustContactName.value = (contactPartyDetails?.party?.person && contactPartyDetails?.party?.party?.name) ? contactPartyDetails.party.party.name : '';
      trustModel.trustContactPosition.value = (contactPartyDetails?.party?.person?.position) ? contactPartyDetails.party.person.position : null;

      // get the contact's phone number
      const phoneDetails: any = await this.clientService.getPartyPhoneList(contactPartyDetails?.party?.party?.iD);
      if (phoneDetails.length > 0) {
        if (phoneDetails.length === 1 && phoneDetails[0].telephoneTypeID === MOBILE) {
          trustModel.trustContactMobile.value = phoneDetails[0]?.TelephoneNumber.telephoneNumber;
        } else {
          const mobileNumbers = _.filter(phoneDetails, (x: any) => x.telephoneTypeID === MOBILE);

          if (mobileNumbers.length > 0) {
            trustModel.trustContactMobile.value = mobileNumbers[0]?.TelephoneNumber.telephoneNumber;
          }
        }
      }

      // get contact's email address
      const emailDetails: any = await this.clientService.getPartyEmailAddresses(contactPartyDetails?.party.party.iD);
      if (emailDetails.length > 0) {
        const preferredEmail = _.find(emailDetails, (x: any) => x.preferredEmail);
        trustModel.trustContactEmail.value = (preferredEmail) ? preferredEmail.EmailAddress.emailAddress : emailDetails[0].EmailAddress.emailAddress;
      }
    }

    if (data.address) {
      if (data?.address?.trading) {
        const crumbsAddress: any = (data.address.trading.id) ? await this.getCrumbsAddressRecord(data.address.trading.id) : null;
        trustModel.tradingAddress.value = (crumbsAddress && crumbsAddress.addressText) ? this.mapAddressModel(crumbsAddress) : this.mapAddressModel(data.address.trading);
      }

      if (data?.address?.postal) {
        const crumbsAddress: any = (data.address.postal.id) ? await this.getCrumbsAddressRecord(data.address.postal.id) : null;
        trustModel.postalAddress.value = (crumbsAddress && crumbsAddress.addressText) ? this.mapAddressModel(crumbsAddress) : this.mapAddressModel(data.address.postal);
      }

      if (data?.address?.postSettlement) {
        const crumbsAddress: any = (data.address.postSettlement.id) ? await this.getCrumbsAddressRecord(data.address.postSettlement.id) : null;
        trustModel.postSettlementAddress.value = (crumbsAddress && crumbsAddress.addressText) ? this.mapAddressModel(crumbsAddress) : this.mapAddressModel(data.address.postSettlement);
      }
    }

    if (data.income && Object.keys(data.income).length > 0) {
      Object.keys(data.income).forEach((key: string) => {
        const rentalIncomes: any[] = _.filter(data.income[key], (x: any) => x.sourceType === 'rental');
        if (rentalIncomes.length > 0) {
          this.addInvestmentIncome(rentalIncomes, servicingModel, servicingData[0], parties);
        }
      });
    }

    trustList.value.push(trustModel);
  }

  /**
   * Add new company to the companyList
   * @param data
   * @param companyList
   * @param parties
   */
  async addNewCompany(data: any, companyList: any, parties: any) {
    const companyModel = new IndividualCompanyModel();
    companyModel.companyName.value = data.name;
    companyModel.companyABN.value = (data.abn && this.validateAbnAcn(data.abn)) ? _.trim(data.abn) : null;
    companyModel.companyACN.value = (data.acn && this.validateAbnAcn(data.acn, 'acn')) ? _.trim(data.acn) : null;
    companyModel.industryType.value = (data.industry) ? data.industry : null;
    companyModel.partyRoleId.value = (data.partyRoleId) ? data.partyRoleId : null;
    companyModel.partyId.value = (data.partyId) ? data.partyId : null;
    companyModel.trusteeOf.value = (data.trusteeOf) ? data.trusteeOf : null;

    // get contact and contact's details
    if (data.contact) {
      const contactPartyDetails: any = await this.validatePartyRole(data.contact);
      companyModel.companyContactName.value = (contactPartyDetails?.party?.person && contactPartyDetails?.party?.party?.name) ? contactPartyDetails.party.party.name : '';
      companyModel.companyContactPosition.value = (contactPartyDetails?.party?.person?.position) ? contactPartyDetails.party.person.position : null;

      // get the contact's phone number
      const phoneDetails: any = await this.clientService.getPartyPhoneList(contactPartyDetails?.party?.party?.iD);
      if (phoneDetails.length > 0) {
        if (phoneDetails.length === 1 && phoneDetails[0].telephoneTypeID === MOBILE) {
          companyModel.companyContactMobile.value = phoneDetails[0]?.TelephoneNumber.telephoneNumber;
        } else {
          const mobileNumbers = _.filter(phoneDetails, (x: any) => x.telephoneTypeID === MOBILE);

          if (mobileNumbers.length > 0) {
            companyModel.companyContactMobile.value = mobileNumbers[0]?.TelephoneNumber.telephoneNumber;
          }
        }
      }

      // get contact's email address
      const emailDetails: any = await this.clientService.getPartyEmailAddresses(contactPartyDetails?.party.party.iD);
      if (emailDetails.length > 0) {
        const preferredEmail = _.find(emailDetails, (x: any) => x.preferredEmail);
        companyModel.companyContactEmail.value = (preferredEmail) ? preferredEmail.EmailAddress.emailAddress : emailDetails[0].EmailAddress.emailAddress;
      }
    }

    if (data.address) {
      if (data?.address?.trading) {
        const crumbsAddress: any = (data.address.trading.id) ? await this.getCrumbsAddressRecord(data.address.trading.id) : null;
        companyModel.tradingAddress.value = (crumbsAddress && crumbsAddress.addressText) ? this.mapAddressModel(crumbsAddress) : this.mapAddressModel(data.address.trading);
      }

      if (data?.address?.postal) {
        const crumbsAddress: any = (data.address.postal.id) ? await this.getCrumbsAddressRecord(data.address.postal.id) : null;
        companyModel.postalAddress.value = (crumbsAddress && crumbsAddress.addressText) ? this.mapAddressModel(crumbsAddress) : this.mapAddressModel(data.address.postal);
      }

      if (data?.address?.postSettlement) {
        const crumbsAddress: any = (data.address.postal.id) ? await this.getCrumbsAddressRecord(data.address.postSettlement.id) : null;
        companyModel.postSettlementAddress.value = (crumbsAddress && crumbsAddress.addressText) ? this.mapAddressModel(crumbsAddress) : this.mapAddressModel(data.address.postSettlement);
      }
    }

    companyList.value.push(companyModel);
  }

  /**
   * Adds the employment and incomes models (including investment incomes)
   * @param data
   * @param appEmploymentDetails
   * @param servicingModel
   * @param servicingData
   * @param parties
   */
  addEmploymentIncome(data: any, appEmploymentDetails: any, servicingModel: any, servicingData: any, parties: any) {
    const employmentArrayModel = new EmploymentGroupsModel();
    appEmploymentDetails.employmentList.value.push(employmentArrayModel);

    if (data.employments && data.employments.length > 0) {
      data.employments.forEach((employment: any, employmentIndex: number) => {
        if (employment.currency && employment.currency === 'current') {
          const employmentCurrentModel = new SingleEmploymentModel();
          employmentCurrentModel.employer.value = employment.employer;
          employmentCurrentModel.occupation.value = employment.occupation;
          employmentCurrentModel.currencyType.value = employment.currency;
          employmentCurrentModel.category.value = employment.categoryType;
          employmentCurrentModel.status.value = employment.status;
          employmentCurrentModel.basis.value = employment.basis;
          employmentCurrentModel.probation.value = employment.probation;

          if (employment.anzsicCategoryType) {
            const anzsicCategory: any = _.find(this.anzsicCategories, (x: any) => x.id === employment.anzsicCategoryType);
            employmentCurrentModel.anzsicCategory.value = (anzsicCategory) ? employment.anzsicCategoryType : null;
          }

          employmentCurrentModel.abnAcnNumber.value = (employment.abn && this.validateAbnAcn(employment.abn)) ? _.trim(employment.abn) : null;
          employmentCurrentModel.businessType.value = (employment.businessType) ? employment.businessType : null;

          if (data.income && Object.keys(data.income).length > 0) {
            Object.keys(data.income).forEach((key: string) => {
              const addBackIncomeType: any = this.addbackIncomeTypes.find((x: any) => x.id === key);

              if (addBackIncomeType) {
              const addBackIncomes: any = (addBackIncomeType) ? _.filter(data.income[key], { 'employmentId': employment.id,  }) : [];
                if (addBackIncomeType && addBackIncomes.length > 0) {
                  const addbackIncomeModel = new SelfEmployedAddbackModel();
                  addBackIncomes.forEach((income: any, incomeIndex: number) => {
                    addbackIncomeModel.addbackType.value = income.sourceType;
                    if (income.currency && income.currency === 'current') {
                      addbackIncomeModel.addbackCurrentYearIncome.value = income.amount;
                    } else if (income.currency && income.currency === 'previous') {
                      addbackIncomeModel.addbackPreviousYearIncome.value = income.amount;
                    }

                    if (addBackIncomes.length === (incomeIndex + 1)) {
                      employmentCurrentModel.allowableAddBacks.value.push(addbackIncomeModel);
                    }
                  });
                }
              } else {
                const employmentIncomes = _.filter(data.income[key], { 'employmentId': employment.id });
                if (!addBackIncomeType && employmentIncomes.length > 0) {
                  employmentIncomes.forEach((income: any) => {
                    if (key === 'income-company-profit-before-tax') {
                      if (income.currency && income.currency === 'current') {
                        employmentCurrentModel.currentYearIncome = income.amount;
                      } else if (income.currency && income.currency === 'previous') {
                        employmentCurrentModel.previousYearIncome = income.amount;
                      }
                    } else {
                      const incomeModel = new BasicIncomeModel();
                      incomeModel.type.value = income.sourceType;
                      incomeModel.frequency.value = income.frequency;
                      employmentCurrentModel.incomes.value.push(incomeModel);
                    }
                  });
                }
              }
            });
          }

          employmentArrayModel.currentEmployments.value.push(employmentCurrentModel);
        }

        if (employment.currency && employment.currency === 'previous') {
          const employmentPreviousModel = new SinglePreviousEmploymentModel();
          employmentPreviousModel.employer.value = employment.employer;
          employmentPreviousModel.occupation.value = employment.occupation;
          employmentPreviousModel.currencyType.value = employment.currency;
          employmentPreviousModel.category.value = employment.category;
          employmentPreviousModel.status.value = employment.status;
          employmentPreviousModel.basis.value = employment.basis;
          employmentPreviousModel.probation.value = employment.probation;
          employmentPreviousModel.lengthYears.value = employment.probation;
          employmentPreviousModel.lengthMonths.value = employment.probation;

          employmentArrayModel.previousEmployments.value.push(employmentPreviousModel);
        }
      });
    }

    // add non-taxable incomes and other incomes
    if (data.income && Object.keys(data.income).length > 0) {
      Object.keys(data.income).forEach((key: string) => {
        const nonEmploymentIncomes: any[] = _.filter(data.income[key], (x: any) => !x.employmentId && !x.employment);
        const rentalIncomes: any[] = _.filter(data.income[key], (x: any) => x.sourceType === 'rental');

        // Add rental/investment incomes
        if (rentalIncomes.length > 0 && !this.justiceApplication) {
          this.addInvestmentIncome(rentalIncomes, servicingModel, servicingData, parties);
        }

        // Add other and non-taxable incomes
        if (nonEmploymentIncomes.length > 0) {
          _.forEach(nonEmploymentIncomes, function(income: any) {
            const incomeModel = new BasicIncomeModel();
            incomeModel.type.value = income.sourceType;
            incomeModel.frequency.value = income.frequency;
            incomeModel.amount.value = income.amount;

            // check income type to determine if other or non-taxable income
            if (_.find(OTHER_INCOME_SOURCE_TYPES, {'id': income.sourceType})) {
              employmentArrayModel.otherIncomes.value.push(incomeModel);
            } else if (_.find(NON_TAXABLE_INCOME_SOURCE_TYPES , {'id': income.sourceType})) {
              employmentArrayModel.nonTaxableIncomes.value.push(incomeModel);
            }
          });
        }
      });
    }
  }

  /**
   * Add invesment incomes
   * @param rentalIncomes
   * @param servicingModel
   * @param servicingData
   * @param parties
   */
  addInvestmentIncome(rentalIncomes: any, servicingModel: any, servicingData: any, parties: any) {
    const {appInvestmentIncome} = servicingModel;
    _.forEach(rentalIncomes, function(income: any) {
      const rentalIncomeModel = new IndividualInvestmentIncomeModel();
      const linkId = (income.identities && income.identities.adhoc) ? _.find(income.identities.adhoc, (x: any) => x.name === 'link' && x.value) : null;

      // check that the investment income has not already been added
      const existingLinkId = _.find(appInvestmentIncome.investmentList.value, (x: any) => x.link.value === linkId.value);

      // only process rental incomes with a link value
      if (linkId && !existingLinkId) {
        const ownershipPercent: any = (income.identities && income.identities.adhoc) ? _.find(income.identities.adhoc, (x: any) => x.name === 'ownership' && x.value) : 100;
        const incomeAmount = (!ownershipPercent || (ownershipPercent && +(ownershipPercent.value) !== 100)) ? ((100 / +(ownershipPercent.value)) * +(income.amount)) : income.amount;

        // process income
        rentalIncomeModel.link.value = linkId.value ? linkId.value : null;
        rentalIncomeModel.rent.value = incomeAmount ? incomeAmount : null;
        rentalIncomeModel.frequencyType.value = income.frequency ? income.frequency : null;

        // process asset
        if (servicingData.assets && servicingData.assets.length > 0) {
          const linkedAsset: any = _.find(servicingData.assets, (x: any) => x.link === linkId.value && x.type === 'investment');

          if (linkedAsset) {
            // add the ownership against the parties based on the linkedAsset
            if (linkedAsset.ownership && linkedAsset.ownership.length > 0) {
              _.forEach(linkedAsset.ownership, (ownership: any) => {
                const party: any = _.find(parties, (x: any) => x.partyId === ownership.id);
                let partyName: string;

                if (party && party.type === 'individual') {
                  partyName = party.firstName + ' ' + party.lastName;
                } else if (party && party.type === 'company') {
                  partyName = party.name;
                }

                rentalIncomeModel.parties.push({
                  id: ownership.id,
                  name: (partyName) ? partyName : '',
                  ownership: +(ownership.value) * 100,
                });
              });
            }

            // commenting out isNewInvestment & refinancing as as existing investment income would no longer be a new investment
            // rentalIncomeModel.isNewInvestment.value = linkedAsset.isNewInvestment ? linkedAsset.isNewInvestment : null;
            // rentalIncomeModel.refinancing.value = linkedAsset.refinancing ? linkedAsset.refinancing : null;

            rentalIncomeModel.securityAddress.value = linkedAsset.securityAddress ? linkedAsset.securityAddress : null;
            rentalIncomeModel.assetValue.value = linkedAsset.amount ? linkedAsset.amount : null;
            rentalIncomeModel.isHighDensity.value = linkedAsset.isHighDensity ? linkedAsset.isHighDensity : null;
            rentalIncomeModel.propertyType.value = linkedAsset.propertyType ? linkedAsset.propertyType : null;
            rentalIncomeModel.useAsSecurity.value = linkedAsset.useAsSecurity ? linkedAsset.useAsSecurity : null;
          }
        }

        // process liability
        if (servicingData.liabilities && servicingData.liabilities.length > 0) {
          const linkedLiability: any = _.find(servicingData.liabilities, (x: any) => x.link === linkId.value && x.type === 'investment');

          if (linkedLiability) {
            rentalIncomeModel.monthlyRepayments.value = linkedLiability.amount ? linkedLiability.amount : null;
            rentalIncomeModel.creditProvider.value = (linkedLiability.creditProvider && linkedLiability.creditProvider.partyRoleId) ? linkedLiability.creditProvider.partyRoleId : null;
            rentalIncomeModel.interestOnly.value = linkedLiability.interestOnly ? linkedLiability.interestOnly : null;
            rentalIncomeModel.invLoanIntAddback.value = linkedLiability.invLoanIntAddback ? linkedLiability.invLoanIntAddback : null;
            rentalIncomeModel.investmentLoanBalance.value = linkedLiability.owing ? linkedLiability.owing : null;
            rentalIncomeModel.frequencyType.value = linkedLiability.frequencyType ? linkedLiability.frequencyType : null;
          }
        }

        appInvestmentIncome.investmentList.value.push(rentalIncomeModel);
      }
    });
  }

  addCollateralLiability(servicingModel: any, submissionModel: any, originator: any) {
    const {appLiabilitiesDetails} = servicingModel;
    const {purposes} = submissionModel;

    // Add the home loan to liabilities
    if (purposes && purposes.length > 0) {
      console.log('originator', JSON.stringify(originator, null, 2));
      const fmcCreditProvider: any = _.find(this.creditProviders, (x: any) => x.ext?.Slug === 'firstmac-limited');
      const lcaCreditProvider: any = _.find(this.creditProviders, (x: any) => x.ext?.Slug === 'loans-com-au');
      const creditProvider: any = (originator.id === LCA_ORIGINATOR) ? lcaCreditProvider : (originator.id === FMC_ORIGINATOR) ? fmcCreditProvider :
        (_.find(this.creditProviders, (x: any) => x.ext?.CompanyName === originator.name)) ? _.find(this.creditProviders, (x: any) => x.ext?.CompanyName === originator.name).id : null;

      _.forEach(purposes, function (purpose: any) {
        const liabilityModel = new IndividualLiabilityModel();
        liabilityModel.type.value = 'home-loan';
        liabilityModel.limitValue.value = purpose?.product && purpose?.product?.originalLoanAmount ? +(purpose?.product?.originalLoanAmount) : 0;
        liabilityModel.amount.value = purpose?.product && purpose?.product.monthlyRepayment ? +(purpose?.product.monthlyRepayment) : 0;
        liabilityModel.lender.value = (creditProvider) ? +(creditProvider?.id) : null;
        appLiabilitiesDetails.liabilitiesList.value.push(liabilityModel);
      });
    }
  }

  /**
   * Add servicing to each of the servicing models (ownership.id = partyId)
   * @param servicingModel
   * @param servicingData
   * @param submissionModel
   * @param parties
   */
  addServicing(servicingModel: any, servicingData: any, submissionModel: any, parties: any) {
    const {appAssetsDetails, appLiabilitiesDetails, appExpensesDetails} = servicingModel;
    const {purposes} = submissionModel;

    // Add expenses
    if (servicingData.expenses && servicingData.expenses.length > 0) {
      _.forEach(servicingData.expenses, function (expense: any) {
        const expenseModel = new IndividualExpenseModel();
        expenseModel.type.value = expense?.type ? expense.type : null;
        expenseModel.amount.value = expense?.amount ? expense.amount : null;
        appExpensesDetails.expensesList.value.push(expenseModel);
      });
    }

    // Add assets
    if (servicingData.assets && servicingData.assets.length > 0) {
      const filteredAssets: any = _.filter(servicingData.assets, (x: any) => (x.type !== 'investment'));
      const investmentAssets: any = _.filter(servicingData.assets, (x: any) => (x.type === 'investment' && x.link));

      if (investmentAssets && investmentAssets.length > 0) {
        _.forEach(investmentAssets, function (investmentAsset: any) {
          const linkedInvestmentAssets: any = _.find(servicingData.liabilities, (x: any) => (x.link === investmentAsset.link));
          if (!linkedInvestmentAssets) {
            console.log('Count not find linkedInvestmentAssets', investmentAsset);
            filteredAssets.push(investmentAsset);
          }
        });
      }

      _.forEach(filteredAssets, function (asset: any) {
        const assetModel = new IndividualAssetModel();
        assetModel.type.value = asset?.type ? asset.type : null;
        assetModel.amount.value = asset?.amount ? asset.amount : null;
        assetModel.description.value = asset?.description ? asset.description : null;
        assetModel.refinance.value = asset?.refinance ? asset.refinance : null;
        appAssetsDetails.assetsList.value.push(assetModel);
      });
    }

    // Add liabilities
    if (servicingData.liabilities && servicingData.liabilities.length > 0) {
      const filteredLiabilities: any = _.filter(servicingData.liabilities, (x: any) => (x.type !== 'investment'));
      const investmentLiabilities: any = _.filter(servicingData.liabilities, (x: any) => (x.type === 'investment' && x.link));

      if (investmentLiabilities && investmentLiabilities.length > 0) {
        _.forEach(investmentLiabilities, function (investmentLiability: any) {
          const linkedInvestmentLiabilities: any = _.find(servicingData.assets, (x: any) => (x.link === investmentLiability.link));
          if (!linkedInvestmentLiabilities) {
            console.log('Count not find linkedInvestmentAssets', investmentLiability);
            filteredLiabilities.push(investmentLiability);
          }
        });
      }

      _.forEach(filteredLiabilities, function (liability: any) {
        const liabilityModel = new IndividualLiabilityModel();
        liabilityModel.type.value = liability?.type ? liability.type : null;
        liabilityModel.amount.value = liability?.amount ? liability.amount : 0;
        liabilityModel.description.value = liability?.description ? liability.description : null;
        liabilityModel.refinance.value = liability?.refinance ? liability?.refinance : liability?.refinancing ? liability?.refinancing : null;
        liabilityModel.limitValue.value = liability?.limitValue ? liability.limitValue : liability?.owing ? liability.owing : 0;
        liabilityModel.lender.value = liability?.lender ? liability.lender : liability?.creditProvider ? liability?.creditProvider : null;
        liabilityModel.interestOnly.value = liability?.interestOnly ? liability.interestOnly : null;
        liabilityModel.postSettlement.value = liability?.postSettlement ? liability.postSettlement : liability.postSettlementOwing ? liability.postSettlementOwing : 0;
        appLiabilitiesDetails.liabilitiesList.value.push(liabilityModel);
      });
    }
  }

  /**
   * Fetch the crumbs address record, but reduces api requests by storing retrieved address records to the addressRecords array
   * @param addressId
   */
  async getCrumbsAddressRecord(addressId: number) {
    const existingAddressRecord = this.addressRecords.find((x: any) => x.iD === addressId);

    if (existingAddressRecord) {
      return existingAddressRecord;
    } else {
      const crumbsAddress: any = await firstValueFrom(this.clientService.getAddress(addressId));
      this.addressRecords.push(crumbsAddress);
      return crumbsAddress;
    }
  }

  /**
   * Maps the address model to a formControl
   * @param data
   */
  mapAddressModel(data) {
    const country: any = this.countries.find((x: any) => x.id === data.countryID);
    const state: any = this.australianStateTypes.find((x: any) => (x.id === data.stateID) || (x.id === data.stateId));

    const addressModel: any = {};
    addressModel.addressId = (data.iD) ? data.iD : (data.id) ? data.id : null;
    addressModel.flatSearch = (data.addressText) ? data.addressText : null;
    addressModel.rPDataPropertyID = (data.rPDataPropertyID) ? data.rPDataPropertyID : (data.rpId) ? data.rpId : null;
    addressModel.poBox = (data.pOBox) ? data.pOBox : (data.poBox) ? data.poBox : null;
    addressModel.unitNumber = (data.unitNumber) ? data.unitNumber : null;
    addressModel.streetNumber = (data.streetNumber) ? data.streetNumber : null;
    addressModel.propertyName = (data.propertyName) ? data.propertyName : null;
    addressModel.streetName = (data.streetName) ? data.streetName : null;
    addressModel.streetType = (data.streetType) ? data.streetType : null;
    addressModel.streetTypeId = (data.streetTypeID) ? data.streetTypeID : null;
    addressModel.suburb = (data.suburb) ? data.suburb : null;
    addressModel.stateId = (data.stateID) ? data.stateID : (data.stateId) ? data.stateId : null;
    addressModel.state = (state) ? state.text : (data.stateText) ? data.stateText : (data.state) ? data.state : null;
    addressModel.postcode = (data.postcode) ? data.postcode : (data.postCode) ? data.postCode : null;
    addressModel.fullAddress = (data.addressText) ? data.addressText : null;
    addressModel.isManualEntry = false;
    addressModel.isPoBox = false;
    addressModel.streetTypeSlug = (data.streetTypeSlug) ? data.streetTypeSlug : null;
    addressModel.stateSlug = (state && state.ext) ? state.ext.Slug : (data.stateSlug) ? data.stateSlug : null;
    addressModel.country = (data.country) ? data.country : (country) ? country.ext.Slug : null;
    addressModel.countryId = (data.countryID) ? data.countryID : null;
    addressModel.countrySlug = (data.countrySlug) ? data.countrySlug : (country) ? country.ext.Slug : null;

    return addressModel;
  }

  public validateAbnAcn(input, mode = 'abn') {
    console.log('validateAbnAcn', input, mode);

    const charArr = input.split('');
    let isValid = false;
    let sum = 0;
    let weight = 0;

    if (charArr.length === 11 && mode === 'abn') {
      // validate an ABN
      weight = 1;
      // get sum of each digit * weight
      for (let i = 0; i < charArr.length; i++) {
        if (i === 0) {
          // on the first digit, subtract by 1
          // and multiply by 10
          sum = (charArr[i] - 1) * 10;
        } else {
          // succeding digit's weight starts at 1, with increments of 2
          sum += charArr[i] * weight;
          weight += 2;
        }
      }
      // check the remainder, 0 is valid
      if (sum % 89 === 0) {
        isValid = true;
      } else {
        isValid = false;
      }
    } else if (charArr.length === 9) {
      // validate an ACN
      weight = 8;
      for (let i = 0; i < charArr.length; i++) {
        // last digit is the check digit
        if (i === charArr.length - 1) {
          // get calculated complement
          // by getting the remainder of the sum divided by 10
          // then subtract the sum from 10
          let complement = 10 - (sum % 10);
          // if the calculated complement is 10, set it as 0
          if (complement === 10) {
            complement = 0;
          }
          // finally, compare the calculated complement to the last digit
          if (complement === Number(charArr[i])) {
            isValid = true;
          } else {
            isValid = false;
          }
        } else {
          // get sum of the first 8 digits
          sum += charArr[i] * weight;
          // weight starts at 8, decreasing by 1
          weight -= 1;
        }
      }
    }

    return isValid;
  }

  public getTypesData(): Observable<any> {
    return new Observable(observer => {
      this.typesService.getTypes(this.typesList).subscribe(
        lists => {
          lists.forEach((list, index) => {
            this[_.camelCase(this.typesList[index])] = list;

            if (this.typesList[index] === 'income-source-types') {
              const otherIncomeTypes = ['salary', 'other', 'nonspousal', 'interst-addback'];
              const nonTaxableIncomeTypes = ['nontax'];
              OTHER_INCOME_SOURCE_TYPES = list.filter(x => x.ext.incomeCategory && otherIncomeTypes.includes(x.ext.incomeCategory) && !x.ext.companyIncome && !x.ext.isSMSF);
              NON_TAXABLE_INCOME_SOURCE_TYPES = list.filter(x => x.ext.incomeCategory && nonTaxableIncomeTypes.includes(x.ext.incomeCategory) && !x.ext.companyIncome && !x.ext.isSMSF);
            }
          });
          observer.next(null);
          observer.complete();
        },
        error => {
          console.log(error);
          observer.error(error);
        });
    });
  }
}
